/**
 * The shopping list, and checking things off in the aisle.
 *
 * Tap targets here are deliberately large. This screen is used one-handed, in a shop, holding
 * something else — it is the moment the whole loop either closes or does not.
 */

import { useState } from 'preact/hooks';

import type { Actions } from '../actions';
import type { ShoppingEntry } from '../api/types';
import { outstanding } from '../domain/shopping';

interface Props {
  shopping: ShoppingEntry[];
  actions: Actions;
  onChanged: () => void;
}

export function Shopping({ shopping, actions, onChanged }: Props) {
  const [adding, setAdding] = useState('');
  const toGet = outstanding(shopping);

  const add = async (): Promise<void> => {
    const wanted = adding.trim();
    if (wanted === '') {
      return;
    }

    await actions.addToShoppingList(wanted, null);
    setAdding('');
    onChanged();
  };

  const check = async (entry: ShoppingEntry): Promise<void> => {
    // Where it goes is already answered by the household's own list, and a food that is not on
    // that list is put on it here rather than asked about — standing at a till is the worst
    // possible moment for a question, and it can be corrected later in two taps.
    await actions.purchase(entry);
    onChanged();
  };

  return (
    <div class="shopping">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void add();
        }}
      >
        <input
          type="text"
          value={adding}
          placeholder="Add to the list"
          enterkeyhint="done"
          onInput={(event) => setAdding((event.target as HTMLInputElement).value)}
        />
        <button type="submit" disabled={adding.trim() === ''}>
          Add
        </button>
      </form>

      {toGet.length === 0 ? (
        <p class="empty">Nothing to get. Things you run low on turn up here by themselves.</p>
      ) : (
        <ul class="list">
          {toGet.map((entry) => (
            <li key={entry.id}>
              <button type="button" class="check" onClick={() => void check(entry)}>
                <span class="box" aria-hidden="true" />
                <span class="name">{entry.name}</span>
                {entry.source === 'auto_low' ? <span class="why">ran low</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
